/**
 * Rana announcement bar.
 *
 * Rotates messages with a choice of transitions, or scrolls them as a marquee.
 * Pauses on hover, on keyboard focus, and while the tab is hidden. Honours
 * prefers-reduced-motion by holding on the first message instead of animating.
 */
class RanaAnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-ab-slide]'));
    this.root = this.closest('[data-ab-root]');
    this.progress = this.root?.querySelector('[data-ab-progress] span');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.getAttribute('data-transition') === 'stagger' && !this.reduced) {
      this.slides.forEach((slide) => this.#splitWords(slide));
    }

    this.index = 0;
    this.#activate(0);

    if (this.slides.length < 2 || this.reduced) {
      this.progress?.style.setProperty('animation', 'none');
      return;
    }

    const speed = Number.parseFloat(this.getAttribute('data-speed'));
    this.delay = (Number.isFinite(speed) && speed > 0 ? speed : 6) * 1000;
    this.#start();

    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);

    this.onVisibility = () => (document.hidden ? this.#pause() : this.#resume());
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    this.#stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /**
   * Wraps each word in its own span so it can be revealed on a stagger. Walks
   * text nodes rather than rewriting innerHTML, so rich text and links survive.
   */
  #splitWords(slide) {
    const walker = document.createTreeWalker(slide, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    for (const node of textNodes) {
      if (!node.textContent.trim()) continue;

      const fragment = document.createDocumentFragment();
      for (const part of node.textContent.split(/(\s+)/)) {
        if (!part) continue;
        if (!part.trim()) {
          fragment.append(document.createTextNode(part));
          continue;
        }
        const word = document.createElement('span');
        word.className = 'rana-ab__word';
        word.textContent = part;
        fragment.append(word);
      }
      node.parentNode?.replaceChild(fragment, node);
    }

    slide.querySelectorAll('.rana-ab__word').forEach((word, i) => {
      word.style.setProperty('--rana-ab-word-index', String(i));
    });
  }

  #start = () => {
    this.#stop();
    this.timer = window.setInterval(() => {
      this.index = (this.index + 1) % this.slides.length;
      this.#activate(this.index);
    }, this.delay);
  };

  #stop = () => {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  };

  #pause = () => {
    this.#stop();
    this.root?.classList.add('is-paused');
  };

  #resume = () => {
    this.root?.classList.remove('is-paused');
    this.#start();
  };

  /** Only the visible message is exposed to assistive tech and the tab order. */
  #activate(active) {
    this.slides.forEach((slide, i) => {
      const isActive = i === active;
      slide.classList.toggle('is-active', isActive);
      slide.toggleAttribute('inert', !isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });

    // Restart the progress line in step with the message that just appeared.
    if (this.progress && !this.reduced) {
      this.progress.style.animation = 'none';
      void this.progress.offsetWidth;
      this.progress.style.animation = '';
    }
  }
}

if (!customElements.get('rana-announcement-bar')) {
  customElements.define('rana-announcement-bar', RanaAnnouncementBar);
}

/** Dismissal is remembered per visitor, and reset whenever the copy changes. */
class RanaAnnouncementDismiss extends HTMLElement {
  connectedCallback() {
    this.bar = this.closest('[data-ab-root]');
    this.key = `rana-ab:${this.getAttribute('data-key') || 'default'}`;

    try {
      if (window.localStorage.getItem(this.key) === '1') {
        this.bar?.setAttribute('hidden', '');
        return;
      }
    } catch (error) {
      // Private browsing or blocked storage: show the bar rather than fail.
    }

    this.bar?.removeAttribute('hidden');
    this.querySelector('button')?.addEventListener('click', () => {
      this.bar?.setAttribute('hidden', '');
      try {
        window.localStorage.setItem(this.key, '1');
      } catch (error) {
        // Nothing to persist to; the bar still closes for this page view.
      }
    });
  }
}

if (!customElements.get('rana-announcement-dismiss')) {
  customElements.define('rana-announcement-dismiss', RanaAnnouncementDismiss);
}
